import React, { useState } from "react";
import { LogOut, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import NotificationBell from "./NotificationBell";
import socket from "../socket/socketClient";
import ConfirmDialog from "./ConfirmDialog";

const AgentNavbar = () => {
  const navigate = useNavigate();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    if (socket?.connected) {
      socket.disconnect();
    }
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blue-900 to-blue-800 shadow-lg border-b border-blue-700 z-50">
      {/* ===== MOBILE VIEW (< 640px) ===== */}
      <div className="sm:hidden h-16 flex items-center justify-between px-4">
        {/* Logo only */}
        <div className="flex-shrink-0 p-1 bg-white rounded-lg shadow-sm">
          <img
            src={`${process.env.PUBLIC_URL}/starnutritionlogo.png`}
            alt="Star Nutrition Logo"
            className="h-8 w-14 object-contain"
          />
        </div>
        {/* Actions */}
        <div className="flex items-center gap-3">
          <div className="text-white">
            <NotificationBell />
          </div>
          <button
            onClick={() => setShowLogoutDialog(true)}
            className="text-white hover:bg-blue-700 p-2 rounded-lg transition-all duration-200"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ===== TABLET VIEW (640px - 1024px) ===== */}
      <div className="hidden sm:flex lg:hidden h-20 items-center justify-between px-6">
        {/* Logo + Text */}
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 p-1.5 bg-white rounded-lg shadow-sm">
            <img
              src={`${process.env.PUBLIC_URL}/starnutritionlogo.png`}
              alt="Star Nutrition Logo"
              className="h-10 w-16 object-contain"
            />
          </div>
          <div>
            <h1 className="text-white font-bold text-base tracking-wide">
              Star Nutrition
            </h1>
            <p className="text-blue-100 text-xs">Admin Dashboard</p>
          </div>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-4">
          <div className="text-white">
            <NotificationBell />
          </div>
          <button
            onClick={() => setShowLogoutDialog(true)}
            className="text-white hover:bg-blue-700 p-2 rounded-lg transition-all duration-200"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ===== DESKTOP VIEW (1024px - 1920px) ===== */}
      <div className="hidden lg:flex xl:hidden h-20 items-center justify-between px-8 gap-6">
        {/* Logo + Text + Divider */}
        <div className="flex items-center gap-4 min-w-fit">
          <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-md">
            <img
              src={`${process.env.PUBLIC_URL}/starnutritionlogo.png`}
              alt="Star Nutrition Logo"
              className="h-11 w-18 object-contain"
            />
          </div>
          <div className="border-l border-blue-600 pl-4">
            <h1 className="text-white font-bold text-lg tracking-wide">
              Star Nutrition
            </h1>
            <p className="text-blue-100 text-xs font-medium">Admin Dashboard</p>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1"></div>

        {/* Actions */}
        <div className="flex items-center gap-6">
          <div className="text-white hover:text-blue-100 transition-colors duration-200">
            <NotificationBell />
          </div>
          <button
            onClick={() => setShowLogoutDialog(true)}
            className="text-white hover:bg-blue-700 hover:text-blue-50 p-2 rounded-lg transition-all duration-200"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ===== LARGE DESKTOP VIEW (> 1920px) ===== */}
      <div className="hidden xl:flex h-24 items-center justify-between px-10 gap-8">
        {/* Logo + Text */}
        <div className="flex items-center gap-5 min-w-fit">
          <div className="flex-shrink-0 p-2.5 bg-white rounded-lg shadow-lg">
            <img
              src={`${process.env.PUBLIC_URL}/starnutritionlogo.png`}
              alt="Star Nutrition Logo"
              className="h-14 w-20 object-contain"
            />
          </div>
          <div className="border-l-2 border-blue-600 pl-5">
            <h1 className="text-white font-bold text-xl tracking-wide">
              Star Nutrition
            </h1>
            <p className="text-blue-100 text-sm font-medium">Admin Dashboard</p>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1"></div>

        {/* Actions */}
        <div className="flex items-center gap-8">
          <div className="text-white hover:text-blue-100 transition-colors duration-200">
            <NotificationBell />
          </div>
          <button
            onClick={() => setShowLogoutDialog(true)}
            className="text-white hover:bg-blue-700 hover:text-blue-50 p-3 rounded-lg transition-all duration-200"
            title="Logout"
          >
            <LogOut className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Logout Dialog */}
      <ConfirmDialog
        open={showLogoutDialog}
        title="Logout"
        description="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        onConfirm={() => {
          setShowLogoutDialog(false);
          handleLogout();
        }}
        onCancel={() => setShowLogoutDialog(false)}
        variant="danger"
      />
    </header>
  );
};

export default AgentNavbar;
